import { useTranslation } from "react-i18next";
import { AdminOrgDto, AdminUpdateOrganizationRequest } from "../../../autogen/bff-api";

// buyerLegalType is the one non-boolean setting on the request; the drawer renders it as a select.
export type OrgAccessFlagKey = Exclude<keyof AdminUpdateOrganizationRequest, "name" | "buyerLegalType">;

export interface OrgAccessFlag {
  key: OrgAccessFlagKey;
  label: string;
}

export interface OrgAccessGroup {
  title: string;
  chipBg: string;
  chipFg: string;
  flags: OrgAccessFlag[];
}

export const useOrgAccessGroups = (): OrgAccessGroup[] => {
  const { t } = useTranslation();
  return [
    {
      title: t("Modules"),
      chipBg: "smChipTealBg",
      chipFg: "smChipTealFg",
      flags: [
        { key: "hasBuyerAccess", label: t("Buyer") },
        { key: "hasContractAccess", label: t("Contracts") },
        { key: "hasProjectAccess", label: t("Projects") },
        { key: "hasProductAccess", label: t("Products") },
        { key: "hasNeedsAccess", label: t("Needs") },
      ],
    },
    {
      title: t("Sourcing"),
      chipBg: "smChipGreenBg",
      chipFg: "smChipGreenFg",
      flags: [
        { key: "hasNegotiationAccess", label: t("Negotiations") },
        { key: "canSeeDeliveredBidsBeforeDeadline", label: t("See delivered bids before deadline") },
      ],
    },
    {
      title: t("Signing"),
      chipBg: "smChipAmberBg",
      chipFg: "smChipAmberFg",
      flags: [
        { key: "hasSignatureAccess", label: t("E-signing") },
        { key: "hasBankIdAccess", label: t("BankID") },
      ],
    },
    {
      title: t("AI & Fency"),
      chipBg: "smChipVioletBg",
      chipFg: "smChipVioletFg",
      flags: [
        { key: "hasAiAccess", label: t("AI features") },
        { key: "hasAssistantAccess", label: t("SourceMagnet assistant") },
        { key: "hasAiBidInsightAccess", label: t("AI insight on received bids") },
        { key: "hasFencyContractSearchAccess", label: t("Fency contract search") },
        { key: "fencySyncEnabled", label: t("Fency sync") },
      ],
    },
    {
      title: t("Other"),
      chipBg: "smChipBlueBg",
      chipFg: "smChipBlueFg",
      flags: [
        { key: "hasDocumentTemplateAccess", label: t("Document templates") },
        { key: "hasIFRSAccess", label: t("IFRS") },
        { key: "hasOrganizationAssessmentAccess", label: t("Organization assessment") },
        { key: "devToolsEnabled", label: t("Dev tools") },
      ],
    },
  ];
};

export const getOrgAccessValues = (org: AdminOrgDto): Record<OrgAccessFlagKey, boolean> => ({
  hasBuyerAccess: org.subscription.hasBuyerAccess,
  hasContractAccess: org.subscription.hasContractAccess,
  hasProjectAccess: org.subscription.hasProjectAccess,
  hasProductAccess: org.subscription.hasProductAccess,
  hasNeedsAccess: org.subscription.hasNeedsAccess,
  hasNegotiationAccess: org.subscription.hasNegotiationAccess,
  canSeeDeliveredBidsBeforeDeadline: org.canSeeDeliveredBidsBeforeDeadline,
  hasSignatureAccess: org.subscription.hasSignatureAccess,
  hasBankIdAccess: org.subscription.hasBankIdAccess,
  hasAiAccess: org.subscription.hasAiAccess,
  hasAssistantAccess: org.subscription.hasAssistantAccess,
  hasAiBidInsightAccess: org.subscription.hasAiBidInsightAccess,
  hasFencyContractSearchAccess: org.subscription.hasFencyContractSearchAccess,
  fencySyncEnabled: org.subscription.fencySyncEnabled,
  hasDocumentTemplateAccess: org.subscription.hasDocumentTemplateAccess,
  hasIFRSAccess: org.subscription.hasIFRSAccess,
  hasOrganizationAssessmentAccess: org.subscription.hasOrganizationAssessmentAccess,
  devToolsEnabled: org.subscription.devToolsEnabled ?? false,
});
